import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import {
  Loader2, CalendarDays, Clock, ChevronRight, CheckCircle,
  Zap, Repeat, ArrowLeft, MessageSquare, Package, AlertTriangle,
  Truck, X, MapPin, Info
} from 'lucide-react';
import IndividualBottomNav from '@/components/IndividualBottomNav';
import IndividualSidebar from '@/components/IndividualSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback hardcoded slots if none configured in admin
const DEFAULT_TIME_SLOTS = [
  '09:00 – 10:00',
  '10:00 – 11:00',
  '11:00 – 12:00',
  '12:00 – 13:00',
  '13:00 – 14:00',
  '14:00 – 15:00',
  '15:00 – 16:00',
  '16:00 – 17:00',
  '17:00 – 18:00',
  '18:00 – 19:00',
  '19:00 – 20:00',
];

const IndividualNewOrder = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('individualToken');

  const [activePlan, setActivePlan] = useState(null);
  const [pickupDate, setPickupDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [instructions, setInstructions] = useState('');
  const [totalClothes, setTotalClothes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);

  // Dynamic slots state
  const [availableSlots, setAvailableSlots] = useState([]);
  const [holidayInfo, setHolidayInfo] = useState(null); // { reason }
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Delivery charge info
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [showDeliveryPopup, setShowDeliveryPopup] = useState(false);

  const today = new Date();
  const minDate = format(today, 'yyyy-MM-dd');
  const maxDate = format(addDays(today, 14), 'yyyy-MM-dd');

  useEffect(() => {
    if (!token) { navigate('/individual/login'); return; }
    fetchPlan();
    fetchDeliveryInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlan = async () => {
    try {
      const r = await axios.get(`${API}/individual/plans/active`, { headers: { Authorization: `Bearer ${token}` } });
      setActivePlan(r.data.plan || null);
    } catch { /* ignore */ }
  };

  const fetchDeliveryInfo = async () => {
    try {
      const r = await axios.get(`${API}/individual/delivery-charge-info`, { headers: { Authorization: `Bearer ${token}` } });
      setDeliveryInfo(r.data);
    } catch { /* ignore */ }
  };

  const fetchSlotsForDate = async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    setHolidayInfo(null);
    setAvailableSlots([]);
    setTimeSlot('');
    try {
      const r = await axios.get(`${API}/individual/available-slots?date=${date}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.holiday) {
        setHolidayInfo({ reason: r.data.reason });
      } else if (r.data.slots && r.data.slots.length > 0) {
        setAvailableSlots(r.data.slots);
      } else if (r.data.no_slots_configured) {
        // Admin hasn't set up slots yet — show all defaults
        setAvailableSlots(DEFAULT_TIME_SLOTS);
      } else {
        // Admin has explicitly disabled all slots for this day
        setAvailableSlots([]);
      }
    } catch {
      setAvailableSlots(DEFAULT_TIME_SLOTS);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setPickupDate(date);
    fetchSlotsForDate(date);
  };

  const handleSubmit = async () => {
    if (!pickupDate) { toast.error('Please select a pickup date'); return; }
    if (!timeSlot) { toast.error('Please select a time slot'); return; }

    // Show delivery charge popup if distance > threshold before confirming
    if (deliveryInfo && deliveryInfo.distance_km > deliveryInfo.distance_threshold_km && !showDeliveryPopup) {
      setShowDeliveryPopup(true);
      return;
    }

    await submitOrder();
  };

  const submitOrder = async () => {
    setShowDeliveryPopup(false);
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/individual/schedule-pickup`,
        {
          pickup_date: pickupDate,
          pickup_time_slot: timeSlot,
          special_instructions: instructions || null,
          total_clothes: totalClothes ? parseInt(totalClothes) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrderId(res.data.order?.id);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to schedule pickup');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
        <IndividualSidebar />
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-24 md:pb-0">
          <div className="bg-white border border-slate-200/80 rounded-[2rem] p-10 max-w-sm w-full shadow-sm flex flex-col items-center text-center gap-5">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-emerald-500" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Pickup Scheduled!</h2>
              <p className="text-sm text-slate-500">Our delivery boy will arrive at your address on <span className="font-semibold text-slate-700">{pickupDate}</span> between <span className="font-semibold text-slate-700">{timeSlot}</span>.</p>
              <p className="text-xs text-slate-400 mt-2">He will inspect your clothes and log the services at your doorstep.</p>
            </div>
            <div className="w-full space-y-2">
              <button
                onClick={() => navigate('/individual/orders')}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-2xl shadow-md shadow-orange-200 transition-all hover:-translate-y-0.5 text-sm"
              >
                View My Orders
              </button>
              <button
                onClick={() => { setSuccess(false); setPickupDate(''); setTimeSlot(''); setInstructions(''); setTotalClothes(''); setAvailableSlots([]); setHolidayInfo(null); }}
                className="w-full border border-slate-200 text-slate-600 font-semibold py-3 rounded-2xl text-sm hover:bg-slate-50 transition-all"
              >
                Schedule Another
              </button>
            </div>
          </div>
        </div>
        <IndividualBottomNav />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/individual/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Schedule Pickup</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Page header — Schedule Pickup */}
            <div className="hidden md:block">
              <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Schedule a Pickup</h1>
              <p className="text-sm text-slate-500 mt-0.5">Our delivery boy will come to your address and inspect your clothes</p>
            </div>

            {/* Delivery charge info banner */}
            {deliveryInfo && deliveryInfo.distance_km !== null && (
              <div className={`flex items-start gap-3 rounded-2xl px-4 py-3 border ${
                deliveryInfo.distance_km > deliveryInfo.distance_threshold_km
                  ? 'bg-amber-50 border-amber-100'
                  : 'bg-emerald-50 border-emerald-100'
              }`}>
                <Truck className={`w-4 h-4 flex-shrink-0 mt-0.5 ${deliveryInfo.distance_km > deliveryInfo.distance_threshold_km ? 'text-amber-500' : 'text-emerald-500'}`} />
                <div className="flex-1">
                  {deliveryInfo.distance_km > deliveryInfo.distance_threshold_km ? (
                    <p className="text-xs text-amber-700 font-medium">
                      Your address is <span className="font-bold">{deliveryInfo.distance_km} km</span> from our plant.
                      Since our driver will be traveling more than {deliveryInfo.distance_threshold_km} km, please ensure your order value exceeds
                      ₹{deliveryInfo.order_free_threshold}. Otherwise, delivery charges of
                      <span className="font-bold"> ₹{deliveryInfo.charge > 0 ? deliveryInfo.charge : Math.round(deliveryInfo.distance_km * deliveryInfo.rate_per_km)}</span> will apply.
                    </p>
                  ) : (
                    <p className="text-xs text-emerald-700 font-medium">
                      Your address is <span className="font-bold">{deliveryInfo.distance_km} km</span> from our plant. You qualify for <span className="font-bold">free delivery</span>!
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
              <h3 className="text-sm font-black text-slate-700 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>How it works</h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: CalendarDays, color: 'bg-blue-50 text-blue-600', step: '1', label: 'Schedule', desc: 'Pick date & time' },
                  { icon: Package, color: 'bg-orange-50 text-orange-600', step: '2', label: 'Pickup', desc: 'Boy arrives & inspects' },
                  { icon: CheckCircle, color: 'bg-emerald-50 text-emerald-600', step: '3', label: 'Delivery', desc: 'Cleaned within 72 hrs' },
                ].map(({ icon: Icon, color, step, label, desc }) => (
                  <div key={step} className="flex flex-col items-center text-center gap-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-700">{label}</p>
                      <p className="text-[10px] text-slate-400">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Date selection */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CalendarDays className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="text-base font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Pickup Date</h3>
              </div>
              <input
                type="date"
                value={pickupDate}
                onChange={handleDateChange}
                min={minDate}
                max={maxDate}
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-orange-300 focus:border-orange-500 text-sm outline-none transition-colors font-medium"
              />
            </div>

            {/* Time slot — conditional rendering */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-violet-500" />
                </div>
                <h3 className="text-base font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Preferred Time</h3>
              </div>

              {!pickupDate ? (
                <p className="text-sm text-slate-400 text-center py-2">Please select a date first</p>
              ) : loadingSlots ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                </div>
              ) : holidayInfo ? (
                <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-2xl px-4 py-4">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-700">Week Off / Public Holiday</p>
                    <p className="text-xs text-red-600 mt-0.5">{holidayInfo.reason}</p>
                    <p className="text-xs text-slate-500 mt-1">Please select a different date.</p>
                  </div>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-4">
                  <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-700">No Pickup Slots Available</p>
                    <p className="text-xs text-amber-600 mt-0.5">No pickup slots are available on this date. Please select a different date or contact us.</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {availableSlots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => setTimeSlot(slot)}
                      className={`px-3 py-3 rounded-xl border text-sm font-semibold transition-all ${
                        timeSlot === slot
                          ? 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-violet-300'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Optional details */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm space-y-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-emerald-500" />
                </div>
                <h3 className="text-base font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Optional Details</h3>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1.5">Approx. number of clothes</label>
                <input
                  type="number"
                  min="1"
                  placeholder="e.g. 10"
                  value={totalClothes}
                  onChange={e => setTotalClothes(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1.5">Special Instructions</label>
                <textarea
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                  placeholder="Any specific instructions for our team..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors resize-none"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !pickupDate || !timeSlot || !!holidayInfo}
              className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-black py-4 rounded-2xl shadow-lg shadow-orange-200 transition-all hover:-translate-y-0.5 text-base"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
              {submitting ? 'Scheduling...' : 'Confirm Pickup'}
            </button>

            {/* FoldPass promo link */}
            {activePlan ? (
              <>
                <button onClick={() => navigate('/individual/foldpass')}
                  className="w-full flex items-center gap-3 bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 rounded-[2rem] p-4 text-left hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Repeat className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-black text-blue-800">{activePlan.plan_name} Active</p>
                    <p className="text-xs text-blue-500">{(activePlan.total_kg - activePlan.used_kg).toFixed(1)} kg remaining · Order 4kg+ for free delivery</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
                </button>
                {(() => {
                  const remaining = Math.max(0, activePlan.total_kg - (activePlan.used_kg || 0));
                  return remaining <= 4 && remaining > 0 ? (
                    <div className="mt-2 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-2xl px-3 py-2.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-orange-700">
                        <strong>Only {remaining.toFixed(1)} kg left.</strong> Delivery charges (₹6/km) will apply for this pickup.
                        {' '}Consider renewing — remaining KGs carry over.{' '}
                        <button onClick={() => navigate('/individual/foldpass')} className="underline font-bold">Renew plan →</button>
                      </p>
                    </div>
                  ) : null;
                })()}
              </>
            ) : (
              <button onClick={() => navigate('/individual/foldpass')}
                className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-[2rem] p-4 text-left hover:border-blue-300 hover:shadow-md transition-all">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Repeat className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-black text-slate-800">Get FoldPass Plan</p>
                  <p className="text-xs text-slate-400">Pre-pay for laundry and save more</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
            )}

          </div>
        </main>
      </div>

      <IndividualBottomNav />

      {/* Delivery Charge Popup */}
      {showDeliveryPopup && deliveryInfo && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Truck className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h3 className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Delivery Charges</h3>
                <p className="text-xs text-slate-500">Please note before confirming</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm font-bold text-amber-800">{deliveryInfo.distance_km} km from our plant</span>
              </div>
              <p className="text-sm text-amber-700">
                Since our driver will be traveling more than <span className="font-bold">{deliveryInfo.distance_threshold_km} km</span>, please ensure your order value exceeds <span className="font-bold">₹{deliveryInfo.order_free_threshold}</span>. Otherwise, delivery charges of <span className="font-bold">₹{Math.round(deliveryInfo.distance_km * deliveryInfo.rate_per_km)}</span> will apply.
              </p>
            </div>

            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 mb-5">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">Delivery charge is calculated at ₹{deliveryInfo.rate_per_km}/km. It will be added automatically at checkout.</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeliveryPopup(false)}
                className="flex-1 py-3 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={submitOrder}
                className="flex-1 py-3 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold shadow-md shadow-orange-200 transition-all"
              >
                Confirm Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualNewOrder;
